import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

/**
 * ServiceRateHistory Component
 * Displays and manages rate history for a service
 * Now supports backdating rates as long as no invoices exist in the date range
 *
 * Props:
 * - serviceId: UUID of the service
 * - serviceName: Name of the service (for display)
 * - currentRate: Current rate from service table (fallback display)
 * - onRateChange: Callback when rates are modified (optional)
 */
const ServiceRateHistory = ({
  serviceId,
  serviceName,
  currentRate,
  onRateChange,
}) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [earliestAvailableDate, setEarliestAvailableDate] = useState(null);

  // Form state
  const [newRate, setNewRate] = useState("");
  const [newEffectiveDate, setNewEffectiveDate] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { tenant_id } = useContext(AuthContext);

  // Get tomorrow's date as default for new rates
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Fetch rate history
  const fetchRates = async () => {
    if (!serviceId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await secureAxios.get(
        `/api/services/${serviceId}/rates`
      );
      const fetchedRates = response.data.rates || [];
      setRates(fetchedRates);

      // If the current rate from history differs from the prop, notify parent
      const activeRate = fetchedRates.find((r) => r.is_current);
      if (activeRate && onRateChange) {
        const historyRate = parseFloat(activeRate.rate);
        const propRate = parseFloat(currentRate);
        if (historyRate && historyRate !== propRate) {
          onRateChange(historyRate);
        }
      }
    } catch (err) {
      console.error("Error fetching rate history:", err);
      setError("Failed to load rate history");
    } finally {
      setLoading(false);
    }
  };

  // Fetch earliest uninvoiced date for validation hint
  const fetchEarliestUninvoicedDate = async () => {
    if (!serviceId) return;

    try {
      const response = await secureAxios.get(
        `/api/services/${serviceId}/rates/earliest-uninvoiced`
      );
      setEarliestAvailableDate(response.data.earliest_available_date);
    } catch (err) {
      console.error("Error fetching earliest uninvoiced date:", err);
      // Non-critical, just won't show the hint
    }
  };

  useEffect(() => {
    fetchRates();
    fetchEarliestUninvoicedDate();
  }, [serviceId]);

  // Handle adding a new rate
  const handleAddRate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await secureAxios.post(`/api/services/${serviceId}/rates`, {
        rate: parseFloat(newRate),
        effective_date: newEffectiveDate,
        notes: newNotes || null,
      });

      // Reset form and refresh
      setNewRate("");
      setNewEffectiveDate("");
      setNewNotes("");
      setShowAddForm(false);
      await fetchRates();
      await fetchEarliestUninvoicedDate();

      if (onRateChange) onRateChange();
    } catch (err) {
      console.error("Error adding rate:", err);
      setError(err.response?.data?.error || "Failed to add rate");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle updating a rate
  const handleUpdateRate = async (e) => {
    e.preventDefault();
    if (!editingRate) return;

    setSubmitting(true);
    setError(null);

    try {
      await secureAxios.put(`/api/service-rates/${editingRate.rate_id}`, {
        rate: parseFloat(newRate),
        effective_date: newEffectiveDate,
        notes: newNotes,
      });

      setEditingRate(null);
      setNewRate("");
      setNewEffectiveDate("");
      setNewNotes("");
      await fetchRates();

      if (onRateChange) onRateChange();
    } catch (err) {
      console.error("Error updating rate:", err);
      setError(err.response?.data?.error || "Failed to update rate");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle deleting a rate
  const handleDeleteRate = async (rateId, rateName) => {
    if (!window.confirm(`Are you sure you want to delete this future rate?`)) {
      return;
    }

    try {
      await secureAxios.delete(`/api/service-rates/${rateId}`);
      await fetchRates();
      if (onRateChange) onRateChange();
    } catch (err) {
      console.error("Error deleting rate:", err);
      setError(err.response?.data?.error || "Failed to delete rate");
    }
  };

  // Start editing a rate
  const startEditing = (rate) => {
    setEditingRate(rate);
    setNewRate(rate.rate.toString());
    setNewEffectiveDate(rate.effective_date.split("T")[0]);
    setNewNotes(rate.notes || "");
    setShowAddForm(false);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingRate(null);
    setNewRate("");
    setNewEffectiveDate("");
    setNewNotes("");
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Parse as local date to avoid UTC timezone shift (e.g. 2026-02-28 showing as Feb 27)
    const parts = dateStr.toString().split("T")[0].split("-");
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format currency (for totals and general amounts)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Format rate (preserves up to 3 decimals when needed, e.g. $0.725)
  const formatRate = (amount) => {
    const num = parseFloat(amount) || 0;
    const decimalPart = num.toString().split(".")[1] || "";
    const decimals = Math.min(Math.max(decimalPart.length, 2), 3);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Check if selected date is in the past
  const isDateInPast = (dateStr) => {
    if (!dateStr) return false;
    const selectedDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate < today;
  };

  if (loading) {
    return (
      <div className="card mt-3">
        <div className="card-body">
          <p className="text-muted mb-0">Loading rate history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card mt-3">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="bi bi-clock-history me-2"></i>
          Rate History
        </h6>
        {!showAddForm && !editingRate && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setShowAddForm(true);
              setNewEffectiveDate(getTomorrowDate());
            }}
          >
            <i className="bi bi-plus-lg me-1"></i>
            Add Future Rate
          </button>
        )}
      </div>

      <div className="card-body">
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        {/* Add/Edit Form */}
        {(showAddForm || editingRate) && (
          <form
            onSubmit={editingRate ? handleUpdateRate : handleAddRate}
            className="mb-4 p-3 bg-light rounded"
          >
            <h6 className="mb-3">
              {editingRate ? "Edit Future Rate" : "Add Future Rate"}
            </h6>

            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Rate *</label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    className="form-control"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    required
                    placeholder="0.000"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <label className="form-label">Effective Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={newEffectiveDate}
                  onChange={(e) => setNewEffectiveDate(e.target.value)}
                  required
                />
                {/* Show helpful hint about date selection */}
                {!editingRate && (
                  <small className="text-muted">
                    {isDateInPast(newEffectiveDate) ? (
                      <span className="text-warning">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        Backdating allowed if no invoices exist after this date
                      </span>
                    ) : earliestAvailableDate ? (
                      <>
                        Can backdate to {formatDate(earliestAvailableDate)}{" "}
                        (after last invoiced log)
                      </>
                    ) : (
                      <>Can be any date (no invoiced logs found)</>
                    )}
                  </small>
                )}
                {editingRate && (
                  <small className="text-muted">
                    Future rates only - must be after today
                  </small>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  className="form-control"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="e.g., 2026 rate adjustment"
                />
              </div>
            </div>

            <div className="mt-3">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Saving...
                  </>
                ) : editingRate ? (
                  "Update Rate"
                ) : (
                  "Add Rate"
                )}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  cancelEditing();
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Rate History Table */}
        {rates.length === 0 ? (
          <p className="text-muted mb-0">
            No rate history available. Current rate:{" "}
            {formatRate(currentRate)}
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>Rate</th>
                  <th>Effective Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => (
                  <tr
                    key={rate.rate_id}
                    className={
                      rate.is_current
                        ? "table-success"
                        : rate.is_future
                        ? "table-info"
                        : ""
                    }
                  >
                    <td>
                      <strong>{formatRate(rate.rate)}</strong>
                    </td>
                    <td>{formatDate(rate.effective_date)}</td>
                    <td>{formatDate(rate.end_date)}</td>
                    <td>
                      {rate.is_current && (
                        <span className="badge bg-success">Current</span>
                      )}
                      {rate.is_future && (
                        <span className="badge bg-info">Scheduled</span>
                      )}
                      {rate.is_past && (
                        <span className="badge bg-secondary">Past</span>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">{rate.notes || "-"}</small>
                    </td>
                    <td>
                      {rate.is_future ? (
                        <div className="btn-group btn-group-sm">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => startEditing(rate)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteRate(rate.rate_id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      ) : (
                        <small className="text-muted">
                          <i className="bi bi-lock me-1"></i>
                          Locked
                        </small>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Help text */}
        <div className="mt-3">
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Past and current rates cannot be modified. Only future rates can be
            edited or deleted. When generating invoices, the system uses the
            rate that was effective on the date of service.
            {earliestAvailableDate && (
              <>
                {" "}
                Backdating is allowed to {formatDate(
                  earliestAvailableDate
                )}{" "}
                (the day after the last invoiced log).
              </>
            )}
          </small>
        </div>
      </div>
    </div>
  );
};

export default ServiceRateHistory;